import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CurrentUser } from '@interacta-shared/data-access-auth';
import { selectCurrentTheme } from '@interacta-shared/data-access-theme';
import { IconButtonComponent, ImageComponent } from '@interacta-shared/ui';
import { AIPanelLayoutComponent } from '@interacta-shared/ui-common';
import { areEqualObjects, isDefined, uuid } from '@interacta-shared/util';
import { AICreditActions, AICreditSelectors } from '@interacta/data-access-ai';
import { GenericPost } from '@interacta/util-post';
import {
    getAIConvoHistoryTitle,
    getAIConvoPanelPageTitle,
} from '@modules/ai-convo/helpers/ai-convo-panel.utils';
import { AIConvoAgent } from '@modules/ai-convo/models/ai-convo-agent.model';
import {
    AIConvoContext,
    AIConvoContextType,
} from '@modules/ai-convo/models/ai-convo-context.model';
import { AIConvoHistoryItem } from '@modules/ai-convo/models/ai-convo-history.model';
import { AIConvoPanelView } from '@modules/ai-convo/models/ai-convo-panel.model';
import { AISharedActions } from '@modules/ai-shared/store/ai-shared.actions';
import {
    dashboard,
    dashboardCommunity,
    digitalWorkplaceHome,
} from '@modules/app-routing/routes';
import * as PostSelectors from '@modules/communities/store/post/post.selectors';
import { AppSelectors } from '@modules/core/store';
import { AppRouterState } from '@modules/core/store/app-router.serializer';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest, distinctUntilChanged, filter, map } from 'rxjs';
import { AIConvoActions } from '../../store/ai-convo.actions';
import { selectActiveAIConvoChat } from '../../store/ai-convo.selectors';
import { AIConvoAgentsPanelComponent } from '../ai-convo-agents-panel/ai-convo-agents-panel.component';
import { AIConvoChatAreaComponent } from '../ai-convo-chat-area/ai-convo-chat-area.component';
import { AIConvoHistoryPanelComponent } from '../ai-convo-history-panel/ai-convo-history-panel.component';
import { AIConvoRenameDialogComponent } from '../ai-convo-rename-dialog/ai-convo-rename-dialog.component';
import { AIConvoSettingsPanelComponent } from '../ai-convo-settings-panel/ai-convo-settings-panel.component';

@Component({
    selector: 'interacta-ai-convo-side-panel',
    templateUrl: './ai-convo-side-panel.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AIConvoAgentsPanelComponent,
        AIConvoChatAreaComponent,
        AIConvoHistoryPanelComponent,
        AIConvoRenameDialogComponent,
        AIConvoSettingsPanelComponent,
        AIPanelLayoutComponent,
        IconButtonComponent,
        ImageComponent,
        NgClass,
        TranslatePipe,
    ],
})
export class AIConvoSidePanelComponent {
    private readonly actions = inject(Actions);
    private readonly store = inject(Store);

    readonly currentUser = input<CurrentUser | null>();
    readonly expandedSidebar = input(false);

    readonly expandSidebar = output<boolean>();

    private readonly chatArea = viewChild(AIConvoChatAreaComponent);

    private readonly currentTheme = this.store.selectSignal(selectCurrentTheme);

    readonly pageStack = signal<AIConvoPanelView[]>([]);
    readonly settingsRendered = signal(false);
    readonly agentsListRendered = signal(false);
    readonly historyListRendered = signal(false);
    readonly ANIMATION_DURATION = 300;

    readonly currentView = computed(() => this.pageStack().at(-1) ?? null);
    readonly pageTitle = computed(() => {
        const view = this.currentView();
        return view ? (getAIConvoPanelPageTitle(view) ?? null) : null;
    });

    readonly activeView = computed(() => this.pageStack().at(-1) ?? 'chat');

    readonly showSettings = computed(() => {
        const view = this.currentView();
        return view === 'settings' || view === 'agents' || view === 'history';
    });

    readonly showAgentsList = computed(() => this.currentView() === 'agents');
    readonly showHistoryList = computed(() => this.currentView() === 'history');

    readonly currentThemeMode = computed(() => this.currentTheme().mode);

    private readonly activeChat = this.store.selectSignal(
        selectActiveAIConvoChat,
    );
    readonly chatTitle = computed(() => this.activeChat()?.title ?? null);

    readonly tokenLimitExceeded = this.store.selectSignal(
        AICreditSelectors.selectCreditLimitExceeded,
    );

    // A single rename dialog is shared by both history lists (settings preview
    // and full history), driven by the item being renamed — avoids mounting one
    // dialog per row.
    readonly renamingItem = signal<AIConvoHistoryItem | null>(null);
    readonly renameDialogTitle = computed(() => {
        const item = this.renamingItem();
        return item ? getAIConvoHistoryTitle(item) : '';
    });

    constructor() {
        this.store.dispatch(AICreditActions.fetchCreditStatus());

        combineLatest([
            this.store
                .select(AppSelectors.selectRouteState)
                .pipe(filter(isDefined)),
            this.store.select(PostSelectors.selectDetail),
        ])
            .pipe(
                map(([route, detailPost]) =>
                    this.getContext(route, detailPost),
                ),
                distinctUntilChanged((prev, curr) =>
                    areEqualObjects(prev, curr),
                ),
                takeUntilDestroyed(),
            )
            .subscribe((context) =>
                this.store.dispatch(AIConvoActions.setContext({ context })),
            );

        this.actions
            .pipe(
                ofType(AIConvoActions.askAboutAttachment),
                takeUntilDestroyed(),
            )
            .subscribe(() => this.pageStack.set([]));
    }

    getContext(
        route: AppRouterState,
        detailPost: GenericPost | undefined,
    ): AIConvoContext | null {
        if (route.appBaseRoute === 'post-detail') {
            const routePostId = Number(route.params['id']);
            if (detailPost && detailPost.id === routePostId) {
                return {
                    type: AIConvoContextType.POST,
                    postId: detailPost.id,
                    communityId: detailPost.communityId,
                };
            }
            return null;
        }

        switch (route.appRoute) {
            case dashboardCommunity: {
                const id = route.params['communityId'];
                return id
                    ? {
                          type: AIConvoContextType.COMMUNITY,
                          communityId: Number(id),
                      }
                    : null;
            }

            case dashboard:
                return { type: AIConvoContextType.DASHBOARD };

            case digitalWorkplaceHome:
                return { type: AIConvoContextType.HOME };

            default:
                return null;
        }
    }

    newChat(): void {
        this.store.dispatch(AIConvoActions.startNewChat({ newChatId: uuid() }));
        this.chatArea()?.focusInput();
    }

    navigate(view: AIConvoPanelView): void {
        if (view === 'settings') {
            this.settingsRendered.set(true);
            this.store.dispatch(AIConvoActions.resetAgents());
            this.store.dispatch(AIConvoActions.fetchAgents());
            this.store.dispatch(AIConvoActions.resetHistory());
            this.store.dispatch(AIConvoActions.fetchHistory({}));
        } else if (view === 'agents') {
            this.agentsListRendered.set(true);
        } else if (view === 'history') {
            this.historyListRendered.set(true);
        }
        requestAnimationFrame(() => this.pageStack.update((s) => [...s, view]));
    }

    back(): void {
        this.pageStack.update((s) => s.slice(0, -1));
    }

    onAgentSelected(agent: AIConvoAgent): void {
        this.store.dispatch(
            AIConvoActions.selectAgent({ agent, newChatId: uuid() }),
        );
        this.pageStack.set([]);
        this.chatArea()?.focusInput();
    }

    onHistoryItemClick(item: AIConvoHistoryItem): void {
        this.store.dispatch(
            AIConvoActions.resumeChat({
                sessionId: item.sessionId,
                historyId: item.id,
                newChatId: uuid(),
                title: getAIConvoHistoryTitle(item),
            }),
        );
        this.pageStack.set([]);
        this.chatArea()?.focusInput();
    }

    onRenameItem(item: AIConvoHistoryItem): void {
        this.renamingItem.set(item);
    }

    onRenameConfirm(title: string): void {
        const item = this.renamingItem();
        if (!item) return;

        this.store.dispatch(
            AIConvoActions.renameSession({ historyId: item.id, title }),
        );
    }

    onDeleteItem(item: AIConvoHistoryItem): void {
        this.store.dispatch(
            AIConvoActions.deleteSession({
                historyId: item.id,
                sessionId: item.sessionId,
            }),
        );
    }

    onNewChatFromSettings(): void {
        this.pageStack.set([]);
        this.newChat();
    }

    closePanel(): void {
        this.store.dispatch(AISharedActions.setIsPanelOpen({ isOpen: false }));
    }
}
